import React from 'react';
import Heading from '../../components/Typography/Heading/Heading';
import SubHeading from '../../components/Typography/SubHeading/SubHeading';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import { getURLTickers } from '../ProfileScreen/util';
import config from '../../config';
import styles from './styles.css';
import {
  accountTypes,
  redirectToAccountFinder
} from '../../flows/shared/utils';
import {
  navigateToLoginFromChecklist,
  taxableTransferTypeHeaderDisplay,
  taxableTransferTypeSubHeaderDisplay,
  taxableTransferTypeDisplay,
  taxableTransferLinkText
} from './utils';

const getTaxableTransferChecklistSection = accountType => {
  if (
    accountType === accountTypes.INDIVIDUAL ||
    accountType === accountTypes.JOINT
  ) {
    return (
      <>
        <ul>
          <li>A minimum of $2,500</li>
          <li>
            {accountType === accountTypes.INDIVIDUAL
              ? 'Social Security number'
              : 'Social Security numbers for all registered account owners'}
          </li>
          <li>Permanent U.S. residence</li>
        </ul>

        <style jsx>{styles}</style>
      </>
    );
  }
  return (
    <>
      <ul>
        <li>A minimum of $1,000</li>
        <li>Social Security numbers</li>
        <li>Permanent U.S. residence for you and the beneficiary</li>
      </ul>
      <style jsx>{styles}</style>
    </>
  );
};

const StepsSection = ({ accountType }) => {
  const mfLinkText = 'Research Mutual Funds';

  return (
    <>
      <section>
        <div className="checklist-section-title">
          Opening an account takes 5-10 minutes:
        </div>
        <dl>
          <ol>
            <li>
              <dt data-name="1">Provide basic investment details</dt>
              <dd>This helps us tailor your investment options.</dd>
            </li>
            <li>
              <dt data-name="2">Choose Mutual Funds for your Transfer</dt>
              <dd>
                <a
                  href="https://www.troweprice.com/personal-investing/tools/fund-research"
                  target="_blank"
                  rel="noopener noreferrer">
                  {mfLinkText}
                </a>{' '}
                or{' '}
                <a
                  href={`${config(
                    'returnUrlPrefix'
                  )}personal-investing/new-account/setup/account-info/brk-checklist/${
                    taxableTransferLinkText[accountType]
                  }`}
                  target="_blank"
                  rel="noopener noreferrer">
                  switch to a Brokerage{' '}
                  {taxableTransferTypeDisplay[accountType]} account
                </a>{' '}
                to invest in ETFs, stocks, and other securities.
              </dd>
            </li>
            <li>
              <dt data-name="3">
                {accountType === accountTypes.INDIVIDUAL &&
                  'Provide personal details'}
                {accountType === accountTypes.JOINT &&
                  'Provide personal details for all registered account owners'}
                {accountType === accountTypes.UGMAUTMA &&
                  'Provide personal details for yourself and the minor'}
              </dt>
              <dd>We&apos;ll use these to secure and verify your account.</dd>
            </li>
            <li className="mailbox">
              <dt>Move assets into your new account</dt>
              <dd>
                Once your account is established, complete the Mutual Fund
                Transfer form from the confirmation page and mail it in.
              </dd>
            </li>
          </ol>
        </dl>
      </section>
      <style jsx>{styles}</style>
    </>
  );
};

const ChecklistSection = ({ accountType }) => {
  return (
    <>
      <section>
        <div className="checklist-section-title">You will need:</div>
        {getTaxableTransferChecklistSection(accountType)}
      </section>
      <style jsx>{styles}</style>
    </>
  );
};

const TaxableTransferChecklistScreen = ({ appState, next }) => {
  const {
    store: { authenticated, accountType }
  } = appState;

  const taxableTransferAccType = taxableTransferTypeHeaderDisplay[accountType];

  // multi-fund buy query check
  // capture tickers to pass to InvestmentReviewScreen
  const multiBuyTickers = getURLTickers(window.location.search);

  return (
    <div className="checklist-screen">
      <Heading text={`Transfer ${taxableTransferAccType}`} />
      <SubHeading
        text={`Consolidate existing ${taxableTransferTypeSubHeaderDisplay[accountType]} into one easy-to-manage taxable account`}
      />

      <div className="qv-row">
        <div className="qv-col col-left">
          <StepsSection accountType={accountType} />
        </div>

        <div className="qv-col col-right">
          <ChecklistSection accountType={accountType} />
        </div>
      </div>

      <div className="button-container">
        {authenticated ? (
          <MFBeaconButton
            variant="primary"
            size="normal"
            id="checklistContinueExisting"
            className="beacon-button-margin-override"
            onClick={next(multiBuyTickers)}
            label="Continue Opening an Account"
            data-name="taxableTransferChecklistContinue"
          />
        ) : (
          <div className="login-buttons">
            <MFBeaconButton
              variant="primary"
              size="normal"
              id="checklistLogin"
              className="beacon-button-margin-override"
              onClick={() => navigateToLoginFromChecklist('/profile')}
              label="Already a Client? Log In to Continue"
              data-name="taxableTransferChecklistLogin"
            />
            <MFBeaconButton
              variant="secondary"
              size="normal"
              id="checklistContinueNew"
              className="beacon-button-margin-override"
              onClick={next(multiBuyTickers)}
              label="New to T. Rowe Price? Start Now"
              data-name="taxableTransferChecklistNewStart"
            />
          </div>
        )}
        <MFBeaconButton
          variant="tertiary"
          size="normal"
          id="checklistBack"
          onClick={redirectToAccountFinder}
          label="Back"
          data-name="taxableTransferChecklistBack"
        />
      </div>
      <PhoneNumber />
      <style jsx>{styles}</style>
    </div>
  );
};

export default TaxableTransferChecklistScreen;
